// Presentación de los reembolsos de enganche del portal. Puro.
import { RefundStatusEnum } from '@fiado/type-kit/bin/retailWizard/enums/RefundStatusEnum';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import {
    NO_CHARGE_LABEL,
    isNoCharge,
    paymentMethodName,
    paymentMethodsLabel,
    toPaymentLines,
} from '@/shared/lib/format/salePaymentLines';
import type { SalePaymentMethodEnum } from '@fiado/type-kit/bin/retailWizard/enums/SalePaymentMethodEnum';
import type { SellerPendingRefundItem } from '@fiado/type-kit/bin/retailWizard/dtos/SellerPendingRefundItem';

export function paymentMethodLabel(method: SalePaymentMethodEnum | null): string {
    return method ? paymentMethodName(method) : 'Sin dato';
}

/** Pendientes primero y el más viejo arriba (es una cola); después los entregados, el más reciente arriba. */
export function sortRefunds(
    refunds: readonly SellerPendingRefundItem[],
): SellerPendingRefundItem[] {
    const pending = refunds
        .filter((r) => r.status === RefundStatusEnum.PENDING)
        .sort((a, b) => a.cancelledAt.localeCompare(b.cancelledAt));
    const delivered = refunds
        .filter((r) => r.status !== RefundStatusEnum.PENDING)
        .sort((a, b) => (b.deliveredAt ?? '').localeCompare(a.deliveredAt ?? ''));
    return [...pending, ...delivered];
}

/** El folio de la venta; la cancelada antes del cierre solo tiene su sesión. */
export function refundReference(refund: SellerPendingRefundItem): string {
    return refund.folio ?? refund.sessionId;
}

/** «Entregado por Laura · 21/oct/2026»; sin nombre registrado no se inventa uno. */
export function deliveredLabel(refund: SellerPendingRefundItem): string {
    const when = refund.deliveredAt ? ` · ${formatDateMx(refund.deliveredAt)}` : '';
    return refund.deliveredByName
        ? `Entregado por ${refund.deliveredByName}${when}`
        : `Entregado${when}`;
}

/** Cómo se cobró el enganche: «Tarjeta + Efectivo» con los montos debajo si hubo varios pagos. */
export function refundPaymentsLabel(refund: SellerPendingRefundItem): {
    label: string;
    amounts: string | null;
} {
    const lines = toPaymentLines(refund.payments);
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- registros anteriores a `payments`
    const method = refund.paymentMethod;
    // `refundPendingCents` es el enganche que se cobró; no baja al entregar.
    if (!lines && isNoCharge(method, refund.refundPendingCents)) {
        return { label: NO_CHARGE_LABEL, amounts: null };
    }
    if (!lines) return { label: paymentMethodLabel(method), amounts: null };
    return {
        label: paymentMethodsLabel(lines),
        amounts:
            lines.length > 1 ? lines.map((l) => formatMoneyCents(l.amountCents)).join(' · ') : null,
    };
}
